'use client';

/**
 * Main Navbar Component
 *
 * Reusable navbar for the entire application.
 * Works in both authenticated and unauthenticated contexts.
 *
 * Mobile: [SidebarToggle] [Logo] ---- [Coins] [Profile]
 * Desktop: [Search] ---- [Nav Links] ---- [Coins] [Profile] [SignOut]
 */
import { useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { useQueryClient } from '@tanstack/react-query';
import { LogOut, Menu, Search, User, UserCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useSignOut } from '@kit/supabase/hooks/use-sign-out';
import { JWTUserData } from '@kit/supabase/types';
import { Trans } from '@kit/ui/trans';

import { useAuthModal } from '~/components/auth/auth-modal-context';
import { CoinBalance } from '~/components/wallet/coin-balance';
import { FirstPurchasePill } from '~/components/wallet/first-purchase-pill';

import { useSidebar } from './sidebar-context';

interface MainNavbarProps {
  user?: JWTUserData | null;
  showCoinBalance?: boolean;
  showStartChat?: boolean;
}

export function MainNavbar({
  user,
  showCoinBalance = true,
  showStartChat = false,
}: MainNavbarProps) {
  const { t } = useTranslation('common');
  const [searchQuery, setSearchQuery] = useState('');
  const router = useRouter();
  const signOut = useSignOut();
  const { openAuthModal } = useAuthModal();
  const { isOpen, setMobileOpen, toggleSidebar, sidebarWidth, isAnimating } =
    useSidebar();
  const queryClient = useQueryClient();

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) {
      router.push(`/catalog?q=${encodeURIComponent(q)}`);
      setSearchQuery('');
    }
  };

  const handleSignOut = async () => {
    // Clear caches immediately so navbar updates even if page reload is slow (mobile)
    queryClient.setQueryData(['supabase:user'], undefined);
    queryClient.removeQueries({ queryKey: ['wallet:balance'] });

    await signOut.mutateAsync();
  };

  return (
    <nav
      className="border-foreground/10 fixed top-[var(--promo-banner-h,0px)] right-0 left-0 z-[120] h-16 overflow-x-hidden border-b"
      style={{ background: 'var(--eros-navbar-bg)' }}
    >
      <div className="flex h-full w-full max-w-full items-center px-2 md:px-3 lg:px-4">
        <div className="flex h-full w-full items-center justify-between gap-2 md:gap-3">
          {/* Mobile: Sidebar Toggle + Logo */}
          <div className="flex items-center gap-2 lg:hidden">
            <button
              onClick={() => setMobileOpen(true)}
              className="hover:text-eros-pink text-foreground hover:bg-foreground/5 rounded-lg p-2 transition-colors"
              aria-label={t('navbar.openSidebarAria')}
            >
              <Menu className="h-5 w-5" />
            </button>
            <Link href="/" className="flex items-center gap-1.5">
              <Image
                src="/images/favicon/logo.webp"
                alt="Edem AI"
                width={32}
                height={32}
                className="h-8 w-8 flex-shrink-0"
                priority
              />
              <span className="text-lg tracking-wide">
                <span className="text-foreground font-bold">Edem</span>
                <span className="text-foreground/70 font-light"> AI</span>
              </span>
            </Link>
          </div>

          {/* Desktop: Sidebar toggle + logo — width is locked to the sidebar so
              this section's right border lines up exactly with the sidebar's
              right border (one continuous vertical line). The -16px accounts for
              the navbar's lg:px-4 left padding. */}
          <div
            className="border-foreground/10 hidden h-full flex-shrink-0 items-center gap-2.5 overflow-hidden border-r lg:flex"
            style={{
              // Open: width = sidebar − 16 (navbar padding), left-aligned.
              // Collapsed: span the full sidebar width and center the burger so
              // it lines up on the same axis as the collapsed sidebar icons.
              width: isOpen ? `${sidebarWidth - 16}px` : `${sidebarWidth}px`,
              marginLeft: isOpen ? undefined : '-16px',
              justifyContent: isOpen ? undefined : 'center',
              transition: isAnimating
                ? 'width 300ms cubic-bezier(0.4, 0, 0.2, 1), margin-left 300ms cubic-bezier(0.4, 0, 0.2, 1)'
                : undefined,
            }}
          >
            <button
              onClick={toggleSidebar}
              className="text-foreground hover:bg-foreground/10 rounded-lg p-2 transition-colors"
              aria-label="Toggle sidebar"
            >
              <Menu className="h-5 w-5" />
            </button>
            {isOpen && (
              <Link href="/" className="flex items-center gap-2">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/images/favicon/logo-64.webp"
                  width={64}
                  height={64}
                  alt="Edem AI"
                  className="h-8 w-8 flex-shrink-0"
                />
                <span
                  className="text-2xl leading-none font-semibold tracking-tight whitespace-nowrap"
                  style={{ fontFamily: 'var(--font-display), Georgia, serif' }}
                >
                  <span className="text-foreground">Edem</span>{' '}
                  <span style={{ color: '#b07cf0' }}>AI</span>
                </span>
              </Link>
            )}
          </div>

          {/* Desktop: Search Component - Left side */}
          <form
            onSubmit={handleSearchSubmit}
            className="relative hidden w-auto max-w-[360px] min-w-[220px] lg:block xl:max-w-[440px]"
          >
            <div className="group relative">
              <Search
                className="group-focus-within:text-eros-pink text-muted-foreground absolute transition-colors"
                style={{
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '16px',
                  height: '16px',
                }}
              />
              <input
                type="text"
                placeholder={t('navbar.searchCharactersPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  paddingLeft: '42px',
                  paddingRight: '34px',
                  paddingTop: '10px',
                  paddingBottom: '10px',
                }}
                className="focus:border-eros-pink/50 border-foreground/10 bg-foreground/5 text-foreground placeholder:text-muted-foreground focus:bg-foreground/10 w-full rounded-full border text-sm transition-all focus:shadow-[0_0_20px_rgba(236,72,153,0.15)] focus:outline-none"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  aria-label={t('navbar.clearSearchAria')}
                  className="text-muted-foreground hover:text-foreground absolute transition-colors"
                  style={{
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                >
                  <X
                    style={{ width: '14px', height: '14px' }}
                    aria-hidden="true"
                  />
                </button>
              )}
            </div>
          </form>

          {/* Spacer — nav links removed; pushes the auth section to the right. */}
          <div className="hidden flex-1 lg:block" />

          {/* Auth Section - Desktop */}
          <div className="border-foreground/20 ml-2 hidden flex-shrink-0 items-center gap-2 border-l pl-2 lg:ml-3 lg:flex lg:pl-3 xl:ml-6 xl:gap-3 xl:pl-6">
            {user ? (
              <>
                <FirstPurchasePill />
                {showCoinBalance && <CoinBalance />}
                {showStartChat ? (
                  <Link
                    href="/chat"
                    className="from-eros-pink to-eros-purple rounded-xl bg-gradient-to-r px-2 py-2 text-[11px] font-semibold whitespace-nowrap text-white transition-all hover:shadow-[0_0_20px_rgba(236,72,153,0.4)] lg:px-3 lg:text-xs xl:px-5 xl:text-sm"
                  >
                    <Trans i18nKey="common:navbar.startChat" />
                  </Link>
                ) : (
                  <>
                    <Link
                      href="/account"
                      className="from-eros-pink to-eros-purple flex items-center gap-2 rounded-full bg-gradient-to-r py-1 pr-3 pl-1 text-[11px] font-semibold whitespace-nowrap text-white transition-all hover:shadow-[0_0_20px_rgba(236,72,153,0.4)] lg:text-xs xl:pr-5 xl:text-sm"
                    >
                      <span className="bg-foreground/25 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full">
                        <User className="h-4 w-4 text-white" />
                      </span>
                      <Trans i18nKey="common:navbar.myProfile" />
                    </Link>
                    <button
                      onClick={handleSignOut}
                      className="border-foreground/20 text-foreground rounded-xl border p-2 transition-all hover:border-red-500 hover:text-red-400 hover:shadow-[0_0_15px_rgba(239,68,68,0.2)]"
                      title={t('navbar.signOutAria')}
                    >
                      <LogOut className="h-4 w-4" />
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                <button
                  onClick={() => openAuthModal('sign-in')}
                  className="hover:border-eros-pink hover:text-eros-pink border-foreground/20 text-foreground rounded-xl border px-2 py-2 text-[11px] whitespace-nowrap transition-all hover:shadow-[0_0_15px_rgba(236,72,153,0.2)] lg:px-3 lg:text-xs xl:px-5 xl:text-sm"
                >
                  <Trans i18nKey="common:navbar.login" />
                </button>
                <button
                  onClick={() => openAuthModal('sign-up')}
                  className="from-eros-pink to-eros-purple rounded-xl bg-gradient-to-r px-2 py-2 text-[11px] font-semibold whitespace-nowrap text-white transition-all hover:shadow-[0_0_20px_rgba(236,72,153,0.4)] lg:px-3 lg:text-xs xl:px-5 xl:text-sm"
                >
                  <Trans i18nKey="common:navbar.signUp" />
                </button>
              </>
            )}
          </div>

          {/* Mobile: Auth Section (right side) */}
          <div className="flex items-center gap-1.5 lg:hidden">
            {user ? (
              <>
                <FirstPurchasePill />
                {showCoinBalance && <CoinBalance />}
                <Link
                  href="/account"
                  className="hover:border-eros-pink hover:text-eros-pink border-foreground/20 text-foreground rounded-lg border p-1.5 transition-all"
                  title={t('navbar.myAccountAria')}
                >
                  <UserCircle className="h-4 w-4" />
                </Link>
                <button
                  onClick={handleSignOut}
                  className="border-foreground/20 text-foreground rounded-lg border p-1.5 transition-all hover:border-red-500 hover:text-red-400"
                  title={t('navbar.signOutAria')}
                >
                  <LogOut className="h-3.5 w-3.5" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => openAuthModal('sign-in')}
                  className="hover:border-eros-pink border-foreground/20 text-foreground rounded-xl border px-2.5 py-1.5 text-[10px] whitespace-nowrap transition-colors md:text-xs"
                >
                  <Trans i18nKey="common:navbar.login" />
                </button>
                <button
                  onClick={() => openAuthModal('sign-up')}
                  className="from-eros-pink to-eros-purple rounded-xl bg-gradient-to-r px-2.5 py-1.5 text-[10px] font-semibold whitespace-nowrap text-white transition-all md:text-xs"
                >
                  <Trans i18nKey="common:navbar.signUp" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
